import { useEffect, useState } from 'react';
import styled, { css } from "styled-components"
import ProductCardList from "../../components/product/card/ProductCardList"
import SidebarCollection from "../../components/sidebar/SidebarCollection"
import { TypesetHeadingS } from "../../components/typeset/Typeset"
import { Breakpoint, Columns, MobileBreakpoint } from '../../styles/functions';
import { TypeBodyM, Wrapper } from '../../styles/mixins';
import CollectionOverlayFilter from './CollectionOverlayFilter';
import { useRouter } from 'next/router';
import useCollectionScroll from '../../hooks/useCollectionScroll';
import CollectionMode from './CollectionMode';
import { useInterface } from '../../contexts/InterfaceProvider';

interface CollectionShowProps {
  content,
  caption,
  productsKey,
  initialProducts,
  initialPagination?
}


const CollectionShow: React.FC<CollectionShowProps> = ({ 
  content,
  caption, 
  productsKey,
  initialProducts,
  initialPagination,
}: CollectionShowProps) => {
  const router = useRouter();

  if(!initialProducts) return <p>Products not found</p>

  // Use collection scroll if pagination exists
  // Otherwise just display initial products
  const {
    products,
    triggerRef
  } = {
    ...(initialPagination ? 
      useCollectionScroll({
        initialProducts,
        initialPagination,
      }) : {
        products: initialProducts,
        triggerRef: null,
      }
    ) 
  }

  // Layout settings
  const {collectionLayout} = useInterface()

  // Filter overlay open
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  useEffect(() => {
    setIsOverlayOpen(false);
  }, [router.asPath])

  return (
    <Container>
      <Introduction 
        indent={4} 
        content={content} 
        caption={caption}
        captionAlign="SUPER"
      />

      <ProductsWrap>
        <Sidebar>
          <CollectionMode
            showFilter={true}
            setIsOpen={setIsOverlayOpen}
         />
          <CollectionSidebar />
        </Sidebar>
        <Products 
          $layout={collectionLayout}
          layout={collectionLayout} 
          products={products} 
          key={productsKey}
        />
      </ProductsWrap>

      <CollectionOverlayFilter 
        isOpen={isOverlayOpen} 
        setIsOpen={setIsOverlayOpen}
      />

      <MoreTrigger ref={triggerRef} />
    </Container>
  )
} 


const Container = styled.div`
  margin-top: var(--header-height); 
  margin-bottom: var(--spacing-120);
`

const ProductsWrap = styled.section`
  ${Breakpoint('tablet', 'min')} {
    display: grid;
    grid-template-columns: ${Columns(3,0)} 1fr ${Columns(1)};
    gap: 0 var(--gutter);
  }
`

const Introduction = styled(TypesetHeadingS)`
  ${Wrapper};
  margin-bottom: var(--spacing-64);
`

const Products = styled(ProductCardList)<{$layout}>`
  // Overview card layout
  ${props => 
    props.$layout == "OVERVIEW" && css`
  `}

  // Medium card layout
  ${props => props.$layout == "M" && css`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: var(--spacing-64) var(--gutter);

    ${Breakpoint('tablet', 'max')} {
      ${Wrapper};
      grid-template-columns: 1fr 1fr;
    }

    ${MobileBreakpoint} {
      ${Wrapper};
      grid-template-columns: 1fr;
    }
  `}

  // Small card layout
  ${props => props.$layout == "S" && css`
    display: grid;
    gap: var(--spacing-64) var(--gutter);

    ${Breakpoint('tablet', 'max')} {
      ${Wrapper};
      grid-template-columns: 1fr 1fr;
    }
  `}
`

const Sidebar = styled.div`
  ${TypeBodyM};
  margin-bottom: auto;
  margin-left: var(--inset);

  ${Breakpoint('tablet', 'min')} {
    position: sticky;
    top: var(--header-height);
  }
`

const CollectionSidebar = styled(SidebarCollection)`
  ${Breakpoint('tablet', 'max')} {
    display: none;
  }
`

const MoreTrigger = styled.div``

export default CollectionShow